/**
 * Shared query definitions for public content.
 *
 * Two things this exists to fix:
 *
 * 1. **Fetching in `useEffect`.** That cannot start until the bundle has
 *    downloaded, parsed and hydrated, so the page paints empty and stays
 *    empty for the length of a request. Used from a route `loader` instead,
 *    the fetch begins during navigation and the data ships inside the HTML.
 *
 * 2. **Refetching what we already have.** Every list used to pull all 435
 *    episodes (~600 KB), and opening a show pulled them again. Lists here
 *    seed the per-entity caches they pass over, so a show page renders from
 *    what the explore page already loaded and revalidates in the background.
 *
 * Query keys are hierarchical — `["episodes"]` covers every episode query, so
 * a mutation can invalidate the lot with one call, while `["episode", id]`
 * stays individually addressable.
 */

import type { QueryClient } from "@tanstack/react-query";
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type {
  PublicContributor,
  PublicEpisode,
  PublicShow,
} from "@/lib/explore";

/** Public content changes rarely; this keeps navigation from refetching. */
const PUBLIC_STALE_TIME = 5 * 60 * 1000;

export interface EpisodeFilters {
  from?: string;
  tags?: string[];
  to?: string;
}

export interface EpisodePage {
  episodes: PublicEpisode[];
  nextCursor: string | null;
  total: number;
}

export const queryKeys = {
  contributor: (id: string) => ["contributor", id] as const,
  episode: (id: string) => ["episode", id] as const,
  episodes: ["episodes"] as const,
  episodesList: (
    showId?: string,
    contributorId?: string,
    filters?: EpisodeFilters
  ) =>
    [
      "episodes",
      "list",
      showId ?? null,
      contributorId ?? null,
      filters?.from ?? null,
      filters?.to ?? null,
      filters?.tags ? [...filters.tags].toSorted().join(",") : null,
    ] as const,
  show: (id: string) => ["show", id] as const,
  shows: ["shows"] as const,
  tags: ["tags"] as const,
};

/**
 * Writes each episode into its own cache entry.
 *
 * This is what makes navigation feel instant: by the time someone clicks
 * through to an episode, the list they clicked from has already populated it.
 */
export const seedEpisodes = (
  queryClient: QueryClient,
  episodes: PublicEpisode[]
) => {
  for (const episode of episodes) {
    queryClient.setQueryData(queryKeys.episode(episode.id), episode);
  }
};

const fetchEpisodePage = async (
  showId: string | undefined,
  cursor: string | null,
  limit: number,
  contributorId?: string,
  filters?: EpisodeFilters
): Promise<EpisodePage> => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (showId) {
    params.set("showId", showId);
  }
  if (contributorId) {
    params.set("contributorId", contributorId);
  }
  if (cursor) {
    params.set("cursor", cursor);
  }
  if (filters?.from) {
    params.set("from", filters.from);
  }
  if (filters?.to) {
    params.set("to", filters.to);
  }
  if (filters?.tags?.length) {
    params.set("tags", filters.tags.join(","));
  }
  const data = await apiFetch<EpisodePage>(
    `/api/public/episodes?${params.toString()}`
  );
  return data ?? { episodes: [], nextCursor: null, total: 0 };
};

/**
 * Paged episodes, newest first. `showId` narrows to one show and is part of
 * the key, so a show's pages are cached separately from the global list.
 */
export const episodesInfiniteQuery = (
  showId?: string,
  limit = 30,
  contributorId?: string,
  filters?: EpisodeFilters
) =>
  infiniteQueryOptions({
    getNextPageParam: (lastPage: EpisodePage) => lastPage.nextCursor,
    initialPageParam: null as string | null,
    placeholderData: keepPreviousData,
    queryFn: ({ pageParam }) =>
      fetchEpisodePage(showId, pageParam, limit, contributorId, filters),
    queryKey: queryKeys.episodesList(showId, contributorId, filters),
    staleTime: PUBLIC_STALE_TIME,
  });

/**
 * A single episode. Usually served from the cache seeded by whichever list
 * the visitor arrived from; the fetch is the fallback for a direct link.
 */
export const episodeQuery = (id: string) =>
  queryOptions({
    queryFn: async () => {
      const data = await apiFetch<{ episode: PublicEpisode }>(
        `/api/public/episodes/${id}`
      );
      return data?.episode ?? null;
    },
    queryKey: queryKeys.episode(id),
    staleTime: PUBLIC_STALE_TIME,
  });

export const showsQuery = () =>
  queryOptions({
    queryFn: async () => {
      const data = await apiFetch<{ shows: PublicShow[] }>("/api/public/shows");
      return data?.shows ?? [];
    },
    queryKey: queryKeys.shows,
    staleTime: PUBLIC_STALE_TIME,
  });

export const publicTagsQuery = () =>
  queryOptions({
    queryFn: async () => {
      const data = await apiFetch<{ tags: string[] }>("/api/public/tags");
      return data?.tags ?? [];
    },
    queryKey: queryKeys.tags,
    staleTime: PUBLIC_STALE_TIME,
  });

/**
 * Full records for specific episodes, in one request.
 *
 * Search matches ids locally; the cards still need the icon, recording URL
 * and participants, and fetching them together keeps search results in the
 * same grid as browsing rather than needing a second kind of result UI.
 */
export const episodesByIdsQuery = (ids: string[]) =>
  queryOptions({
    enabled: ids.length > 0,
    queryFn: async () => {
      if (ids.length === 0) {
        return [];
      }
      const data = await apiFetch<EpisodePage>(
        `/api/public/episodes?ids=${encodeURIComponent(ids.join(","))}`
      );
      return data?.episodes ?? [];
    },
    queryKey: ["episodes", "byIds", ids.join(",")] as const,
    staleTime: PUBLIC_STALE_TIME,
  });

/** A contributor's public profile — first name plus catalog fields. */
export const contributorQuery = (id: string) =>
  queryOptions({
    queryFn: async () => {
      const data = await apiFetch<{ contributor: PublicContributor }>(
        `/api/public/contributors/${id}`
      );
      return data?.contributor ?? null;
    },
    queryKey: queryKeys.contributor(id),
    staleTime: PUBLIC_STALE_TIME,
  });

export const showQuery = (id: string) =>
  queryOptions({
    queryFn: async () => {
      const data = await apiFetch<{ show: PublicShow }>(
        `/api/public/shows/${id}`
      );
      return data?.show ?? null;
    },
    queryKey: queryKeys.show(id),
    staleTime: PUBLIC_STALE_TIME,
  });
